import angular from 'angular';
import { SharedAngular } from './@types/sharedAngular';

interface IKendoEditorRange extends Range {
  cloneRange(): IKendoEditorRange;
}

interface IKendoEditor {
  getRange(): IKendoEditorRange;
  selectRange(range: IKendoEditorRange): void;
  value(value?: string): string | void;
  exec(commandName: string, options: { value: string }): void;
  focus(): void;
}

interface IWritingAssistSelection {
  range: IKendoEditorRange | null;
  contentHtml: string;
  editorValue: string;
}

interface IImagePlaceholder {
  token: string;
  image: HTMLImageElement;
}

interface IPreparedWritingAssistContent {
  contentHtml: string;
  imagePlaceholders: IImagePlaceholder[];
}

export class AiEditorService {
  public static $inject = [
    'aiService',
    'notificationService',
    'validationService',
    '$q'
  ];

  private static readonly ERROR_MESSAGE =
    'Unable to improve writing. Please try again.';
  private static readonly INSERT_HTML_COMMAND = 'inserthtml';
  private static readonly IMAGE_PLACEHOLDER_PREFIX = 'IMAGE_';

  private readonly improvingEditors: WeakSet<IKendoEditor> = new WeakSet();

  constructor(
    private aiService: SharedAngular.AiService,
    private notificationService: SharedAngular.NotificationService,
    private validationService: SharedAngular.ValidationService,
    private $q: angular.IQService
  ) {}

  public improveEditor(editor: IKendoEditor): angular.IPromise<void> {
    if (this.improvingEditors.has(editor)) {
      return this.$q.resolve();
    }

    const editorValue = this.getEditorValue(editor);
    const selection = this.getSelection(editor, editorValue);
    const preparedContent = this.replaceImagesWithPlaceholders(
      selection.contentHtml
    );
    this.improvingEditors.add(editor);

    return this.aiService
      .improveHtml(preparedContent.contentHtml)
      .then((improvedHtml: string): void => {
        if (typeof improvedHtml !== 'string' || improvedHtml.length === 0) {
          throw new Error(AiEditorService.ERROR_MESSAGE);
        }

        if (this.getEditorValue(editor) !== selection.editorValue) {
          return;
        }

        const contentWithImages = this.restoreImagePlaceholders(
          improvedHtml,
          preparedContent.imagePlaceholders
        );
        const sanitizedHtml =
          this.validationService.sanitizeHtml(contentWithImages);

        if (selection.range) {
          editor.selectRange(selection.range);
          editor.exec(AiEditorService.INSERT_HTML_COMMAND, {
            value: sanitizedHtml
          });
          return;
        }

        editor.value(sanitizedHtml);
      })
      .catch((): void => {
        this.notificationService.showErrorToast(AiEditorService.ERROR_MESSAGE);
      })
      .finally((): void => {
        this.improvingEditors.delete(editor);
        editor.focus();
      });
  }

  private getEditorValue(editor: IKendoEditor): string {
    return (editor.value() as string) || '';
  }

  private replaceImagesWithPlaceholders(
    contentHtml: string
  ): IPreparedWritingAssistContent {
    const contentContainer: HTMLDivElement = document.createElement('div');
    contentContainer.innerHTML = contentHtml;
    const imagePlaceholders: IImagePlaceholder[] = [];

    contentContainer
      .querySelectorAll('img')
      .forEach((image: HTMLImageElement, index: number): void => {
        const token = `${AiEditorService.IMAGE_PLACEHOLDER_PREFIX}${index}`;
        imagePlaceholders.push({
          token,
          image: image.cloneNode(true) as HTMLImageElement
        });
        const placeholderImage = document.createElement('img');
        placeholderImage.setAttribute('src', token);
        image.replaceWith(placeholderImage);
      });

    return {
      contentHtml: contentContainer.innerHTML,
      imagePlaceholders
    };
  }

  private restoreImagePlaceholders(
    contentHtml: string,
    imagePlaceholders: IImagePlaceholder[]
  ): string {
    if (imagePlaceholders.length === 0) {
      return contentHtml;
    }

    const contentContainer: HTMLDivElement = document.createElement('div');
    contentContainer.innerHTML = contentHtml;
    const placeholderTokens = imagePlaceholders.map(
      ({ token }: IImagePlaceholder): string => token
    );
    const responsePlaceholderTokens = Array.from(
      contentContainer.querySelectorAll('img')
    )
      .map((image: HTMLImageElement): string => image.getAttribute('src') || '')
      .filter((source: string): boolean => placeholderTokens.includes(source));

    if (
      responsePlaceholderTokens.length !== placeholderTokens.length ||
      responsePlaceholderTokens.some(
        (token: string, index: number): boolean =>
          token !== placeholderTokens[index]
      )
    ) {
      throw new Error(AiEditorService.ERROR_MESSAGE);
    }

    imagePlaceholders.forEach(({ token, image }: IImagePlaceholder): void => {
      const matchingImages = Array.from(
        contentContainer.querySelectorAll('img')
      ).filter(
        (responseImage: HTMLImageElement): boolean =>
          responseImage.getAttribute('src') === token
      );
      if (matchingImages.length !== 1) {
        throw new Error(AiEditorService.ERROR_MESSAGE);
      }

      matchingImages[0].replaceWith(image.cloneNode(true));
    });

    return contentContainer.innerHTML;
  }

  private getSelection(
    editor: IKendoEditor,
    editorValue: string
  ): IWritingAssistSelection {
    let range: IKendoEditorRange | null = null;

    try {
      const editorRange = editor.getRange();
      if (editorRange && !editorRange.collapsed) {
        range = editorRange.cloneRange();
      }
    } catch {
      range = null;
    }

    if (!range) {
      return {
        range: null,
        contentHtml: editorValue,
        editorValue
      };
    }

    const container = document.createElement('div');
    container.appendChild(range.cloneContents());

    return {
      range,
      contentHtml: container.innerHTML,
      editorValue
    };
  }
}

angular.module('fg').service('aiEditorService', AiEditorService);

export type AiEditorServiceType = InstanceType<typeof AiEditorService>;
